<template>
  <div class="rs-toast-undo">
    <BToast
      :id="`toast-undo-${id}`"
      v-model="input"
      :title="titleText"
      :header-class="`rs-toast-header`"
      :body-class="`rs-toast-body`"
      no-auto-hide
      no-close-button
      variant="undo"
      no-fade
      toaster="b-toaster-top-right"
    >
      <template #toast-title>
        <div class="pt-2">
          <i class="fas fa-trash-alt fa-lg pr-2 icon" />
          <strong class="toast-title">{{ titleText }}</strong>
        </div>
      </template>
      <div class="row m-0 align-items-center pt-0">
        <div class="pr-2 flex-grow-1 body mb-2">
          {{ bodyText }}
          <a
            class="cancel"
            @click="input = false"
          > CANCEL </a>
        </div>
      </div>
      <div class="rs-toast-progress-bar-container mt-2">
        <div
          class="rs-toast-progress-bar"
          :style="{ transitionDuration: `${delay}ms` }"
          :class="{ 'rs-progress': isStartProgress }"
        />
      </div>
    </BToast>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'RsToastUndo',

  props: {
    id: {
      type: String,
      required: true,
    },

    delay: {
      type: Number,
      default: 10000,
    },

    value: {
      type: Boolean,
      default: false,
    },

    variant: {
      type: String,
      default: 'info',
    },

    initCallback: {
      type: Function,
      // eslint-disable-next-line no-console
      default: () => console.log('Default toast init callback'),
    },

    completeCallback: {
      type: Function,
      // eslint-disable-next-line no-console
      default: () => console.log('Default toast complete callback'),
    },

    undoCallback: {
      type: Function,
      // eslint-disable-next-line no-console
      default: () => console.log('Default toast undo callback'),
    },

    titleText: {
      type: String,
      default: 'Deleting...',
    },

    bodyText: {
      type: String,
      default: 'Deleting Item',
    },

    toastData: {
      type: Object as PropType<Record<string, any>>,
      default: () => undefined,
    },
  },

  data() {
    return {
      timeout: 0,
      input: !!this.value,
      isStartProgress: false,
    };
  },

  watch: {
    value() {
      this.input = !!this.value;

      if (this.input) {
        this.isStartProgress = false;
        setTimeout(() => {
          this.isStartProgress = true;
          this.setTimer();
        }, 200);
      }
    },

    input() {
      if (!this.input && this.timeout) {
        clearTimeout(this.timeout);
        this.undoCallback(this.toastData);
      }

      this.$emit('input', this.input);
    },
  },

  mounted() {
    if (this.input) {
      this.isStartProgress = false;
      setTimeout(() => {
        this.isStartProgress = true;
        this.setTimer();
      }, 200);
    }
  },

  beforeDestroy() {
    if (this.timeout) {
      clearInterval(this.timeout);
    }
  },

  methods: {
    setTimer() {
      this.$nextTick(() => {
        this.initCallback(this.toastData);

        // @ts-ignore
        this.timeout = setTimeout(() => {
          this.input = false;
          this.completeCallback(this.toastData);
        }, this.delay);
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.rs-toast-undo{
  border-left: 20px solid #0B5142;
}
.rs-toast-body {
  background: #C3EDE4;
  color: #0B5142;
  border: 1px solid #0B5142 ;
  border-left: 20px solid #0B5142;
  border-top: none;
  font-weight: 600;
  padding-left: 40px;
}

.cancel{
  float: right;
  color: $black;
  font-weight: bold;
  cursor: pointer;
}

.toast-title{
  margin-left: 4px
}

.rs-toast-header {
  background-clip: initial !important;
  background-color: #C3EDE4 !important;
  border: 1px solid #0B5142 ;
  border-left: 20px solid #0B5142;
  border-bottom: none;
  color: #0B5142 !important;
}

.rs-toast-progress-bar-container {
  border-radius: 0.15rem;
  background: $white;
}
.cancel-button {
  color: #0B5142 !important ;
  &:hover{
          color: $white  !important;
          background-color: #0B5142 !important;
        }
}

.rs-toast-progress-bar {
  background: #0B5142;
  border-radius: 0.15rem;
  height: 0.4rem;
  width: 0px;
  transition-property: width;
  transition-timing-function: linear;

  &.rs-progress {
    transition-property: width;
    width: 100%;
  }
}
</style>
